import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Desfazer do balcão (decisões B3 e B4): a ação aparece feita na hora e a barra de baixo dá 5 s
// para voltar atrás, no lugar de uma confirmação. Uma ação por vez, para o painel inteiro.

export const DESFAZER_MS = 5000;
/** O erro do Desfazer some sozinho depois disto (a barra não fica presa na tela). */
export const ERRO_DO_DESFAZER_MS = 10_000;

export interface AcaoDesfazivel {
  texto: string;
  duracaoMs?: number;
  /** Botão a mais na barra ("Avisar Edson no WhatsApp"). Usar encerra o Desfazer (confirma). */
  extra?: { rotulo: string; href?: string; aoUsar?: () => void };
  /** Volta o que a tela já mostrou feito (ex.: a saída já gravada). */
  aoDesfazer: () => Promise<void> | void;
  /** Grava o que só vale depois dos 5 s (ex.: "Entregue", que não volta no banco). */
  aoConfirmar?: () => Promise<void> | void;
}

export interface EstadoDoDesfazer {
  atual: (AcaoDesfazivel & { id: number; expiraEm: number; desfazendo: boolean }) | null;
  erro: string | null;
  /** Mostra a ação nova; a anterior, se houver, é confirmada na hora. */
  oferecer: (acao: AcaoDesfazivel) => void;
  desfazer: () => void;
  /** Confirma agora e fecha a barra (o "extra" foi usado, ou a pessoa fechou o erro). */
  encerrar: () => void;
}

type AcaoAtual = NonNullable<EstadoDoDesfazer['atual']>;

const Contexto = createContext<EstadoDoDesfazer | null>(null);

/** Segundos que faltam, para o "Desfazer · 4 s" (arredonda para cima: nunca mostra 0 com tempo). */
export function segundosRestantes(expiraEm: number, agora: number = Date.now()): number {
  return Math.max(0, Math.ceil((expiraEm - agora) / 1000));
}

function rodar(f: () => Promise<void> | void): Promise<void> {
  try {
    return Promise.resolve(f());
  } catch (erro) {
    return Promise.reject(erro);
  }
}

function mensagem(erro: unknown, padrao: string): string {
  return erro instanceof Error && erro.message ? erro.message : padrao;
}

export function ProvedorDoDesfazer({ children }: { children: ReactNode }) {
  const [atual, setAtual] = useState<AcaoAtual | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // O estado mais novo para os timers e callbacks estáveis (sem refazer a barra a cada ação).
  const atualRef = useRef<AcaoAtual | null>(null);
  const relogio = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const proximoId = useRef(1);

  const trocar = useCallback((acao: AcaoAtual | null) => {
    atualRef.current = acao;
    setAtual(acao);
  }, []);

  const confirmar = useCallback((acao: AcaoDesfazivel) => {
    if (!acao.aoConfirmar) return;
    rodar(acao.aoConfirmar).catch((e: unknown) =>
      setErro(mensagem(e, 'Não deu para gravar. Confira na lista.')),
    );
  }, []);

  /** Fecha a ação da barra; a que não foi desfeita vale (confirma). */
  const fecharAtual = useCallback(() => {
    clearTimeout(relogio.current);
    const acao = atualRef.current;
    if (!acao) return;
    trocar(null);
    if (!acao.desfazendo) confirmar(acao);
  }, [confirmar, trocar]);

  const oferecer = useCallback(
    (acao: AcaoDesfazivel) => {
      fecharAtual();
      setErro(null);
      const id = proximoId.current++;
      const duracao = acao.duracaoMs ?? DESFAZER_MS;
      const nova: AcaoAtual = { ...acao, id, expiraEm: Date.now() + duracao, desfazendo: false };
      trocar(nova);
      relogio.current = setTimeout(() => {
        if (atualRef.current?.id !== id) return;
        trocar(null);
        confirmar(nova);
      }, duracao);
    },
    [confirmar, fecharAtual, trocar],
  );

  const desfazer = useCallback(() => {
    const acao = atualRef.current;
    if (!acao || acao.desfazendo) return;
    clearTimeout(relogio.current);
    trocar({ ...acao, desfazendo: true });
    setErro(null);
    // Só limpa a barra se ela ainda for desta ação (outra pode ter chegado durante a ida ao banco).
    const limpar = () => {
      if (atualRef.current?.id === acao.id) trocar(null);
    };
    rodar(acao.aoDesfazer).then(limpar, (e: unknown) => {
      limpar();
      setErro(mensagem(e, 'Não deu para desfazer. Confira na lista.'));
    });
  }, [trocar]);

  const encerrar = useCallback(() => {
    fecharAtual();
    setErro(null);
  }, [fecharAtual]);

  useEffect(() => {
    if (!erro) return;
    const id = setTimeout(() => setErro(null), ERRO_DO_DESFAZER_MS);
    return () => clearTimeout(id);
  }, [erro]);

  // Fechar a aba com um "Entregue" esperando os 5 s perde a marca: o navegador pergunta antes.
  useEffect(() => {
    if (!atual?.aoConfirmar || atual.desfazendo) return;
    const avisar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [atual]);

  // Saiu do painel (ex.: sair da conta) com uma ação na barra: ela vale.
  useEffect(
    () => () => {
      clearTimeout(relogio.current);
      const acao = atualRef.current;
      atualRef.current = null;
      if (acao && !acao.desfazendo) confirmar(acao);
    },
    [confirmar],
  );

  const valor = useMemo(
    () => ({ atual, erro, oferecer, desfazer, encerrar }),
    [atual, erro, oferecer, desfazer, encerrar],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useDesfazer(): EstadoDoDesfazer {
  const estado = useContext(Contexto);
  // Sem provedor, um "Entregue" nunca gravaria: melhor quebrar no teste do que sumir em silêncio.
  if (!estado) throw new Error('useDesfazer precisa estar dentro de <ProvedorDoDesfazer>.');
  return estado;
}
